// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  ALL_CURRENCIES,
  IsoCurrencyCode,
  isoCurrencyCodeSchema,
} from '@/currency/constants/iso';
import {
  getAmountPrecisionMessage,
  parseCurrencyAmount,
} from '@/currency/utilities/validate';

const UNPARSEABLE_PASTE_MESSAGE = 'Pasted text is not a valid amount';

/** Resolution of a pasted currency token. */
export type PastedCurrencyToken =
  | { kind: 'none' }
  | { kind: 'unique'; isoCode: IsoCurrencyCode }
  | { kind: 'ambiguous'; candidates: readonly IsoCurrencyCode[] }
  | { kind: 'unrecognized' };

/** A successfully parsed paste: a plain decimal amount string and the
 *  resolution of any currency annotation it carried. */
interface PastedCurrency {
  amount: string;
  token: PastedCurrencyToken;
}

function buildSymbolCandidates(): Map<string, IsoCurrencyCode[]> {
  const map = new Map<string, IsoCurrencyCode[]>();
  for (const code of isoCurrencyCodeSchema.options) {
    const symbol = ALL_CURRENCIES[code].symbol;
    const existing = map.get(symbol);
    if (existing === undefined) {
      map.set(symbol, [code]);
    } else {
      existing.push(code);
    }
  }
  // Bare "$" could be any of the dollar currencies, not just USD.
  map.set(
    '$',
    isoCurrencyCodeSchema.options.filter((code) =>
      ALL_CURRENCIES[code].symbol.includes('$')
    )
  );
  return map;
}

// Symbol -> candidate ISO codes (a single candidate means a unique symbol).
const SYMBOL_CANDIDATES = buildSymbolCandidates();

/** Plain numeric text that the number input can handle natively (caret
 *  insertion); anything else is intercepted as a money paste. */
export function isPlainNumericPaste(text: string): boolean {
  return /^-?\d*\.?\d*$/.test(text.trim());
}

// One number with up to one currency token (symbol or 3-letter code) on each
// side, and an optional minus sign before either.
const MONEY_PASTE_PATTERN =
  /^(?<leadSign>-)?\s*(?<lead>[\p{Sc}\p{L}]{1,3})?\s*(?<innerSign>-)?\s*(?<number>[\d.,]+(?: [\d.,]+)*)\s*(?<trail>[\p{Sc}\p{L}]{1,3})?\s*$/u;

// Exact symbol first, then case-insensitive ISO code; anything else is an
// unrecognized currency token.
function resolveCurrencyToken(token: string): PastedCurrencyToken {
  const candidates = SYMBOL_CANDIDATES.get(token);
  if (candidates !== undefined) {
    return candidates.length === 1
      ? { kind: 'unique', isoCode: candidates[0] }
      : { kind: 'ambiguous', candidates };
  }
  const parsedIso = isoCurrencyCodeSchema.safeParse(token.toUpperCase());
  if (parsedIso.success) {
    return { kind: 'unique', isoCode: parsedIso.data };
  }
  return { kind: 'unrecognized' };
}

// Combines a symbol token and an ISO token (e.g. "$1,234.56 USD"); null when
// the two annotations contradict each other.
function combineTokens(
  a: PastedCurrencyToken,
  b: PastedCurrencyToken
): PastedCurrencyToken | null {
  if (a.kind === 'none') {
    return b;
  }
  if (b.kind === 'none') {
    return a;
  }
  if (a.kind === 'unique' && b.kind === 'unique') {
    return a.isoCode === b.isoCode ? a : null;
  }
  if (a.kind === 'unique' && b.kind === 'ambiguous') {
    return b.candidates.includes(a.isoCode) ? a : null;
  }
  if (a.kind === 'ambiguous' && b.kind === 'unique') {
    return a.candidates.includes(b.isoCode) ? b : null;
  }
  return null;
}

// Strips a grouping separator, requiring 3-digit groups after a lead group
// that cannot start with zero — "0,500" is a decimal, never thousands.
function stripGrouping(value: string, separator: string): string | null {
  const groups = value.split(separator);
  const validLead = /^[1-9]\d{0,2}$/.test(groups[0]);
  const validRest = groups.slice(1).every((group) => /^\d{3}$/.test(group));
  return validLead && validRest ? groups.join('') : null;
}

/**
 * Normalizes a separator-formatted number ("1,234.56", "1.234,56",
 * "1 234,56", "1,234") into a plain decimal string, or null when the shape
 * is malformed. A single separator followed by exactly 3 digits reads as
 * thousands grouping when the leading group permits it.
 */
function normalizeAmount(raw: string): string | null {
  // Spaces are only ever grouping; strip them between digit groups.
  const value = raw.replaceAll(' ', '');
  if (!/\d/.test(value)) {
    return null;
  }

  const lastDot = value.lastIndexOf('.');
  const lastComma = value.lastIndexOf(',');

  if (lastDot !== -1 && lastComma !== -1) {
    // Both present: the later one is the decimal separator.
    const decimalIsDot = lastDot > lastComma;
    const decimalAt = decimalIsDot ? lastDot : lastComma;
    const groupSeparator = decimalIsDot ? ',' : '.';
    const integerPart = value.slice(0, decimalAt);
    const fractionPart = value.slice(decimalAt + 1);
    if (!/^\d*$/.test(fractionPart)) {
      return null;
    }
    const integer = stripGrouping(integerPart, groupSeparator);
    return integer === null ? null : `${integer}.${fractionPart}`;
  }

  const separator = lastDot !== -1 ? '.' : lastComma !== -1 ? ',' : null;
  if (separator === null) {
    return value;
  }

  const occurrences = value.split(separator).length - 1;
  if (occurrences > 1) {
    // Repeated separator can only be grouping.
    return stripGrouping(value, separator);
  }

  const [integerPart, fractionPart] = value.split(separator);
  if (!/^\d*$/.test(integerPart) || !/^\d*$/.test(fractionPart)) {
    return null;
  }
  // A single separator with exactly 3 trailing digits reads as grouping when
  // the leading group is a valid thousands group (which cannot start with
  // zero — "0,500" is European notation for 0.5); otherwise it is a decimal.
  if (fractionPart.length === 3 && /^[1-9]\d{0,2}$/.test(integerPart)) {
    return `${integerPart}${fractionPart}`;
  }
  return `${integerPart}.${fractionPart}`;
}

/**
 * Parses pasted text as a money value: a separator-formatted number with up
 * to one currency token on each side (consistent symbol + ISO pairs like
 * "$1,234.56 USD" are allowed) or wrapped in accounting-format parentheses
 * for a negative. Returns null when the text is not a money value (the
 * paste should be rejected).
 */
export function parsePastedCurrency(text: string): PastedCurrency | null {
  // NBSP and narrow NBSP are the grouping characters Intl/Excel emit.
  let trimmed = text.replaceAll(/[\u00A0\u202F]/g, ' ').trim();

  // Accounting-format negative: the whole value wrapped in parentheses.
  let parenthesizedNegative = false;
  const parenthesized = /^\((.*)\)$/.exec(trimmed);
  if (parenthesized !== null) {
    parenthesizedNegative = true;
    trimmed = parenthesized[1].trim();
  }

  const match = MONEY_PASTE_PATTERN.exec(trimmed);
  if (match === null || match.groups === undefined) {
    return null;
  }
  // Optional named groups are undefined at runtime; the generated type
  // claims plain string, so re-type honestly before the checks below.
  const groups: Partial<Record<string, string>> = match.groups;
  const { leadSign, lead, innerSign, number, trail } = groups;
  if (number === undefined) {
    return null;
  }

  // At most one sign, and never combined with accounting parentheses.
  if (leadSign !== undefined && innerSign !== undefined) {
    return null;
  }
  const sign = leadSign ?? innerSign;
  if (parenthesizedNegative && sign !== undefined) {
    return null;
  }

  const amount = normalizeAmount(number);
  if (amount === null) {
    return null;
  }

  const token = combineTokens(
    lead === undefined ? { kind: 'none' } : resolveCurrencyToken(lead),
    trail === undefined ? { kind: 'none' } : resolveCurrencyToken(trail)
  );
  if (token === null) {
    return null;
  }

  const appliedSign = parenthesizedNegative ? '-' : (sign ?? '');
  return { amount: `${appliedSign}${amount}`, token };
}

/** Outcome of a paste into a money field. */
export type CurrencyPasteResult =
  | { kind: 'accepted'; amount: string; isoCode: IsoCurrencyCode }
  | { kind: 'rejected'; message: string };

/** The money field a paste lands in. */
interface CurrencyPasteTarget {
  isoCode: IsoCurrencyCode;
  selectableCurrencyCodes: readonly IsoCurrencyCode[];
  lockTo?: IsoCurrencyCode;
  allowNegative?: boolean;
  allowZero?: boolean;
}

/** Resolves pasted text to the amount and currency a money field takes. */
export function toCurrencyPasteResult(
  text: string,
  target: CurrencyPasteTarget
): CurrencyPasteResult {
  let amountText = text.trim();
  let token: PastedCurrencyToken = { kind: 'none' };
  if (!isPlainNumericPaste(text)) {
    const parsed = parsePastedCurrency(text);
    if (parsed === null) {
      return { kind: 'rejected', message: UNPARSEABLE_PASTE_MESSAGE };
    }

    amountText = parsed.amount;
    token = parsed.token;
  }

  const amount = parseCurrencyAmount(amountText);
  if (amount === null) {
    return { kind: 'rejected', message: UNPARSEABLE_PASTE_MESSAGE };
  }

  if (amount.isNegative() && !target.allowNegative) {
    return { kind: 'rejected', message: 'Amount cannot be negative' };
  }

  if (amount.isZero() && target.allowZero === false) {
    return { kind: 'rejected', message: 'Amount must be greater than zero' };
  }

  if (token.kind === 'unrecognized') {
    return {
      kind: 'rejected',
      message: 'Pasted text has an unrecognized currency',
    };
  }

  // When locked, a paste may still set the amount but must never switch the
  // currency away from lockTo; a foreign-currency token rejects the paste.
  const pasteCurrencyCodes =
    target.lockTo !== undefined
      ? [target.lockTo]
      : target.selectableCurrencyCodes;
  if (token.kind === 'unique' && !pasteCurrencyCodes.includes(token.isoCode)) {
    return {
      kind: 'rejected',
      message: `${token.isoCode} is not available for this field`,
    };
  }

  if (
    token.kind === 'ambiguous' &&
    !token.candidates.includes(target.isoCode)
  ) {
    return {
      kind: 'rejected',
      message: `Pasted currency does not match ${target.isoCode}`,
    };
  }

  const nextIso =
    token.kind === 'unique' && target.lockTo === undefined
      ? token.isoCode
      : target.isoCode;
  const pastedPrecisionMessage = getAmountPrecisionMessage(amount, nextIso);
  if (pastedPrecisionMessage !== null) {
    return { kind: 'rejected', message: pastedPrecisionMessage };
  }

  return { kind: 'accepted', amount: amount.toString(), isoCode: nextIso };
}
